import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  Trophy,
  Clock,
  HelpCircle,
  BarChart2,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const ColorClashChallenge = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("intro"); // "intro" | "tutorial" | "play" | "results"
  const [difficulty, setDifficulty] = useState(1);

  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const [gameCompleted, setGameCompleted] = useState(false);

  // Trials
  const [trials, setTrials] = useState([]);
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);

  // Stimulus
  const [currentWord, setCurrentWord] = useState("");
  const [currentColor, setCurrentColor] = useState("");
  const [hasResponded, setHasResponded] = useState(false);

  // Scoring & Performance
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [congruentCount, setCongruentCount] = useState(0);
  const [incongruentCount, setIncongruentCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [congruentRTs, setCongruentRTs] = useState([]);
  const [incongruentRTs, setIncongruentRTs] = useState([]);

  // Timing references
  const goSignalTimeRef = useRef(null);

  // UI / tutorial
  const [showInstructions, setShowInstructions] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  // Define color & word sets
  const colorOptions = ["red", "green", "blue", "yellow", "purple"];
  const wordOptions = ["RED", "GREEN", "BLUE", "YELLOW", "PURPLE"];

  // ----------------------------------
  //         FETCH HIGH SCORES
  // ----------------------------------
  useEffect(() => {
    if (!organizationId) return;
    const fetchScores = async () => {
      try {
        const docRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "stroop-challenge"
        );
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const scoresForDifficulty =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scoresForDifficulty);
        } else {
          setHighScores([]);
        }
      } catch (err) {
        console.error("Error fetching high scores:", err);
      }
    };
    fetchScores();
  }, [organizationId, difficulty]);

  // ----------------------------------
  //        GAME & TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying]);

  // Start the game
  // In the startGame function:
  const startGame = () => {
    setScore(0);
    setReactionTimes([]);
    setCongruentCount(0);
    setIncongruentCount(0);
    setCorrectCount(0);
    setIncorrectCount(0);
    setCongruentRTs([]);
    setIncongruentRTs([]);

    const newTrials = generateTrials(getTrialCountForDifficulty(difficulty));
    setTrials(newTrials);
    setCurrentTrialIndex(0);

    // Example time limits by difficulty
    if (difficulty === 1) setTimeLeft(75);
    if (difficulty === 2) setTimeLeft(60);
    if (difficulty === 3) setTimeLeft(45);

    setIsPlaying(true);

    // Immediately display the first trial
    if (newTrials.length > 0) {
      const firstTrial = newTrials[0];
      setCurrentWord(firstTrial.word);
      setCurrentColor(firstTrial.color);
      setHasResponded(false);
      goSignalTimeRef.current = Date.now();
    }

    // This sets up the future trials
    setCurrentTrialIndex(0);
  };

  // End the game
  const endGame = () => {
    setIsPlaying(false);
    setGameMode("results");
    setGameCompleted(currentTrialIndex >= trials.length - 1);
  };

  // Generate trials
  const generateTrials = (numTrials) => {
    const trialsArray = [];
    for (let i = 0; i < numTrials; i++) {
      const word = wordOptions[Math.floor(Math.random() * wordOptions.length)];
      const color =
        colorOptions[Math.floor(Math.random() * colorOptions.length)];

      // ~50% chance of forcing an incongruent trial
      const forceIncongruent = Math.random() < 0.5;
      if (!forceIncongruent) {
        // Congruent
        trialsArray.push({ word, color: word.toLowerCase() });
      } else {
        // Incongruent: ensure color != word
        let newColor = color;
        while (newColor.toUpperCase() === word) {
          newColor =
            colorOptions[Math.floor(Math.random() * colorOptions.length)];
        }
        trialsArray.push({ word, color: newColor });
      }
    }
    return trialsArray;
  };

  // Decide how many trials
  const getTrialCountForDifficulty = (lvl) => {
    if (lvl === 1) return 20;
    if (lvl === 2) return 30;
    return 40;
  };

  // Move to next trial
  const nextTrial = (index, trialList) => {
    if (!isPlaying) return;
    if (index >= trialList.length) {
      endGame();
      return;
    }
    setHasResponded(false);

    const current = trialList[index];
    setCurrentWord(current.word);
    setCurrentColor(current.color);

    setCurrentTrialIndex(index);
    goSignalTimeRef.current = Date.now();
  };

  // Format time
  const formatTime = (sec) => {
    const mins = Math.floor(sec / 60);
    const secs = sec % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // ----------------------------------
  //       HANDLE USER RESPONSE
  // ----------------------------------
  const handleColorResponse = (chosenColor) => {
    if (hasResponded || !isPlaying) return;
    setHasResponded(true);

    const rt = Date.now() - goSignalTimeRef.current;
    setReactionTimes((prev) => [...prev, rt]);

    const trial = trials[currentTrialIndex];
    const isCongruent = trial.word.toLowerCase() === trial.color;
    const isCorrect = chosenColor === trial.color;

    if (isCongruent) {
      setCongruentCount((prev) => prev + 1);
      setCongruentRTs((prev) => [...prev, rt]);
    } else {
      setIncongruentCount((prev) => prev + 1);
      setIncongruentRTs((prev) => [...prev, rt]);
    }

    if (isCorrect) {
      // Reward more for incongruent
      setScore((prev) => prev + (isCongruent ? 10 : 15));
      setCorrectCount((prev) => prev + 1);
    } else {
      // Penalty for errors
      setScore((prev) => (prev > 0 ? prev - 5 : 0));
      setIncorrectCount((prev) => prev + 1);
    }

    // Move to next trial after a short delay
    setTimeout(() => {
      nextTrial(currentTrialIndex + 1, trials);
    }, 400);
  };

  // ----------------------------------
  //         SAVE SCORE
  // ----------------------------------
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // Calculate metrics
      const avgReactionTime =
        reactionTimes.length > 0
          ? Math.round(
              reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
            )
          : 0;
      const avgCongruentRT =
        congruentRTs.length > 0
          ? Math.round(
              congruentRTs.reduce((a, b) => a + b, 0) / congruentRTs.length
            )
          : 0;
      const avgIncongruentRT =
        incongruentRTs.length > 0
          ? Math.round(
              incongruentRTs.reduce((a, b) => a + b, 0) / incongruentRTs.length
            )
          : 0;
      const stroopInterference = avgIncongruentRT - avgCongruentRT;

      // Get user name
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // Create a SINGLE data object to use in both places
      const gameData = {
        // Common metadata
        gameType: "color-clash", // Use the unified game type name here
        gameId: "stroop-challenge", // Keep the original ID for compatibility
        userId: user.email,
        userName,
        mode: "single",
        score,
        difficulty,
        timestamp: new Date(),

        // Game-specific metrics
        correctCount,
        incorrectCount,
        congruentCount,
        incongruentCount,
        avgReactionTime,
        avgCongruentRT,
        avgIncongruentRT,
        stroopInterference,
        totalTrials: trials.length,
      };

      // Save to leaderboard
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "stroop-challenge"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const difficultyKey = difficulty.toString();
        let currentLeaderboard =
          leaderboardData.leaderboards[difficultyKey] || [];

        // Composite comparator
        const compositeComparator = (a, b) => {
          if (a.score !== b.score) return b.score - a.score;
          if (a.stroopInterference !== b.stroopInterference)
            return a.stroopInterference - b.stroopInterference;
          if (a.avgReactionTime !== b.avgReactionTime)
            return a.avgReactionTime - b.avgReactionTime;
          return a.timestamp - b.timestamp;
        };

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[difficultyKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // Save to game history - using the SAME data object
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      setGameMode("intro");
      setGameStarted(false);
    } catch (err) {
      console.error("Error saving Stroop score:", err);
    }
  };

  // ----------------------------------
  //         TUTORIAL LOGIC
  // ----------------------------------
  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-purple-300">
              Color Clash Challenge
            </h1>
            <div className="p-2 bg-purple-500/10 rounded-full">
              <Brain className="text-purple-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions Modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Brain className="text-purple-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Color Clash Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6 text-slate-300">
                  <p>
                    The Color Clash Challenge is a Stroop-like task measuring
                    your ability to suppress the automatic habit of reading the
                    word and instead identify the text color.
                  </p>
                  <p>
                    You’ll see words like “RED,” “BLUE,” or “GREEN,” sometimes
                    matching the font color and sometimes not. Press the button
                    corresponding to the <strong>font color</strong>, ignoring
                    what the word spells.
                  </p>
                  <ul className="list-disc ml-5 space-y-2">
                    <li>Correct answers earn you points.</li>
                    <li>Wrong answers lose points.</li>
                    <li>
                      The difference between your response times on *incongruent
                      vs. congruent* trials measures your impulse control.
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-purple-600 hover:to-indigo-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Outer Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Color Clash Challenge
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Strengthen your selective attention and impulse control by
                  naming the color of the text, not the word itself.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-purple-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-purple-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Inhibitory Control
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Override automatic reading impulses with color-focused
                    responses.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-purple-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-purple-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">Quick Rounds</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Each session is short and tests reaction time under
                    conflicting stimuli.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-purple-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-purple-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Track Progress
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Aim to reduce your “Stroop Interference” over time.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => setDifficulty(lvl)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === lvl
                            ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => setGameMode("play")}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Interference
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((s, index) => {
                          let formattedName = s.userName;
                          if (s.userName && s.userName.includes(" ")) {
                            const parts = s.userName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={`${s.userId}-${index}`}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {s.stroopInterference} ms
                              </td>
                              <td className="py-2 px-4 text-center text-purple-300 font-medium">
                                {s.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Color Clash
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-4 text-slate-300">
                  {tutorialStep === 0 && (
                    <>
                      <p>
                        You’ll see a color word on the screen—sometimes the font
                        color matches the word, sometimes not. **Tap the button
                        for the text color**, ignoring the word itself.
                      </p>
                    </>
                  )}
                  {tutorialStep === 1 && (
                    <>
                      <p>
                        *Congruent* trials: The word text and color match (e.g.,
                        “GREEN” in green letters).
                      </p>
                      <p>
                        *Incongruent* trials: The word text and color conflict
                        (e.g., “GREEN” in red letters). These require more
                        inhibitory control.
                      </p>
                    </>
                  )}
                  {tutorialStep === 2 && (
                    <>
                      <p>
                        You earn more points for correct responses to
                        incongruent items. Wrong answers subtract points.
                      </p>
                      <p>
                        We measure <em>Stroop Interference</em>: the difference
                        in your speed between incongruent and congruent items.
                      </p>
                    </>
                  )}
                  {tutorialStep === 3 && (
                    <>
                      <p>Ready? Let’s begin!</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <button
                    onClick={() => {
                      setGameStarted(true);
                      startGame(); // <-- Add this line
                    }}
                    className="py-3 px-6 bg-gradient-to-r from-purple-500 to-indigo-600 hover:... text-white rounded-lg"
                  >
                    Start Game
                  </button>
                </div>
              )}

              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-purple-500/20 to-indigo-600/20 px-4 py-2 rounded-lg">
                  <span className="text-purple-200 font-medium">
                    Difficulty: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-purple-500/20 to-indigo-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Stimulus display */}
                <div className="relative flex flex-col items-center justify-center w-64 h-64 bg-slate-900/60 border border-slate-700 rounded-xl">
                  <AnimatePresence mode="wait">
                    {currentWord && (
                      <motion.div
                        key={`${currentTrialIndex}-${currentWord}-${currentColor}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                        className="text-4xl font-bold"
                        style={{ color: currentColor }}
                      >
                        {currentWord}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Controls & progress */}
                <div className="md:w-1/3 space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Trial Progress
                    </h3>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-sm">Trial</span>
                      <span className="text-white font-medium">
                        {currentTrialIndex + 1}/{trials.length}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-600"
                        style={{
                          width: `${
                            (currentTrialIndex / trials.length) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {colorOptions.slice(0, 4).map((col) => (
                      <button
                        key={col}
                        onClick={() => handleColorResponse(col)}
                        className="py-3 font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                      >
                        {col.charAt(0).toUpperCase() + col.slice(1)}
                      </button>
                    ))}
                    {/* If you want the 5th color button: */}
                    {colorOptions.length === 5 && (
                      <button
                        onClick={() => handleColorResponse(colorOptions[4])}
                        className="py-3 font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                      >
                        {colorOptions[4].charAt(0).toUpperCase() +
                          colorOptions[4].slice(1)}
                      </button>
                    )}
                  </div>

                  <div>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Session Complete!
                </h2>
                <p className="text-slate-300">
                  Your Color Clash session has ended.
                </p>
              </div>

              {/* Explanation box */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-purple-300">
                    Congruent Trials
                  </span>
                  : Word & color matched; typically easier/faster.
                  <br />
                  <span className="font-medium text-purple-300">
                    Incongruent Trials
                  </span>
                  : Word & color conflicted; tests inhibitory control.
                  <br />
                  <span className="font-medium text-purple-300">
                    Stroop Interference
                  </span>
                  : Difference in reaction times (incongruent vs. congruent).
                  Lower is better!
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-purple-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-purple-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct Answers</span>
                      <span className="text-white">{correctCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Incorrect Answers</span>
                      <span className="text-red-300">{incorrectCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Congruent Trials</span>
                      <span className="text-white">{congruentCount}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Incongruent Trials</span>
                      <span className="text-white">{incongruentCount}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-purple-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    {/* Reaction times */}
                    <div>
                      <p className="text-sm text-slate-300 mb-1">
                        Average Reaction Times
                      </p>
                      <div className="flex justify-between text-sm text-slate-300">
                        <span>Overall</span>
                        <span>
                          {reactionTimes.length > 0
                            ? `${Math.round(
                                reactionTimes.reduce((a, b) => a + b, 0) /
                                  reactionTimes.length
                              )} ms`
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300">
                      <span>Congruent</span>
                      <span>
                        {congruentRTs.length > 0
                          ? `${Math.round(
                              congruentRTs.reduce((a, b) => a + b, 0) /
                                congruentRTs.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between text-sm text-slate-300 mb-2">
                      <span>Incongruent</span>
                      <span>
                        {incongruentRTs.length > 0
                          ? `${Math.round(
                              incongruentRTs.reduce((a, b) => a + b, 0) /
                                incongruentRTs.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="border-t border-slate-700 pt-2 text-sm text-slate-300 flex justify-between">
                      <span>Stroop Interference</span>
                      <span>
                        {incongruentRTs.length > 0 && congruentRTs.length > 0
                          ? `${
                              Math.round(
                                incongruentRTs.reduce((a, b) => a + b, 0) /
                                  incongruentRTs.length
                              ) -
                              Math.round(
                                congruentRTs.reduce((a, b) => a + b, 0) /
                                  congruentRTs.length
                              )
                            } ms`
                          : "N/A"}
                      </span>
                    </div>

                    <div className="mt-4">
                      <p className="text-slate-300 text-sm leading-relaxed">
                        A lower interference means stronger impulse control and
                        selective attention. Keep practicing to reduce that gap!
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameCompleted && (
  <button onClick={saveScore} className="py-3 px-6 bg-gradient-to-r ...">
    <Trophy size={18} />
    Save Score
  </button>
)}


                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorClashChallenge;
